import React, { useState, useEffect } from "react";
import axios from "../../utils/axiosInstance";

const MemberListModal = ({
  visible,
  onCancel,
  onSelectMembers,
  selectedPartners = []
}) => {
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState(selectedPartners);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  useEffect(() => {
    if (visible) {
      fetchMembers();
      setSelectedMembers(selectedPartners);
      setCurrentPage(1); // Reset to first page when modal opens
    }
  }, [visible, selectedPartners]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchQuery]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/user');
      setMembers(response.data?.users || response.data || []);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member =>
    `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / limit);
  const startIndex = (currentPage - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  const handleMemberToggle = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleSave = () => {
    onSelectMembers(selectedMembers);
    onCancel();
  };

  if (!visible) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Select Members</h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>

          <div className="modal-body">
            {/* Search Bar */}
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search members by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Member List */}
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : paginatedMembers.length > 0 ? (
                <div className="list-group">
                  {paginatedMembers.map((member) => (
                    <div
                      key={member._id}
                      className={`list-group-item d-flex align-items-center`}
                    >
                      <input
                        className="form-check-input ml-1"
                        type="checkbox"
                        checked={selectedMembers.includes(member._id)}
                        onChange={() => handleMemberToggle(member._id)}
                      />
                      <div className="d-flex align-items-center flex-grow-1">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center ml-4"
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#e9ecef',
                            color: '#F94F01',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            border: '2px solid #F94F01',
                            userSelect: 'none'
                          }}
                        >
                          {`${(member.firstName?.[0] || '').toUpperCase()}${(member.lastName?.[0] || '').toUpperCase()}`}
                        </div>
                        <div>
                          <h6 className="mb-1">{`${member.firstName} ${member.lastName}`}</h6>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  No members found
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted small">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredMembers.length)} of {filteredMembers.length} members
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={page}
                        className={`btn btn-sm text-primary-custom ${currentPage === page ? 'btn-outline-primary' : 'btn-outline-primary'}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success text-white border-0"
              onClick={handleSave}
            >
              Add Members ({selectedMembers.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberListModal;
